import { ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  DwComponent,
  DwComponentType,
  DwContext,
  DwFormContextMode,
  DwFormControlDateTime2Component,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  formatDateTimeFromLocal,
} from '@devwareapps/devware-cap';
import { filter, take } from 'rxjs/operators';

import {
  PresetDateTimeControlOptions,
  PresetDateTimeFormControlConfigComponent,
  PresetDateTimeOptionKey
} from './preset-date-time-form-control-config.component';

interface PresetDateTimeOption {
  key: PresetDateTimeOptionKey;
  label: string;
  daysToAdd: number;
}

@DwComponent({
  key: 'preset-date-time-form-control',
  name: 'Preset Date/Time Form Control',
  componentType: DwComponentType.formControl,
  isGlobal: true,
  configComponent: PresetDateTimeFormControlConfigComponent,
})
@Component({
  selector: 'app-preset-date-time-form-control',
  templateUrl: './preset-date-time-form-control.component.html',
  styleUrls: ['./preset-date-time-form-control.component.scss']
})
export class PresetDateTimeFormControlComponent extends DwFormControlDateTime2Component implements OnInit {
  @Input() context: DwContext;

  attrName: string;
  dateOptions: PresetDateTimeControlOptions;
  dateTimeControl: AbstractControl;
  useGmtDateTimeValue: boolean;
  use24HourTimeValue: boolean;
  formApi: DwMetaDataFormApi;
  selectedPresetKey: PresetDateTimeOptionKey;
  private addModeDefaultApplied = false;

  readonly presetOptions: PresetDateTimeOption[] = [
    { key: 'tomorrow', label: 'Tomorrow', daysToAdd: 1 },
    { key: 'in-3-days', label: 'In 3 days', daysToAdd: 3 },
    { key: 'next-week', label: 'Next week', daysToAdd: 7 },
  ];

  constructor(
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    private dwFormStateService: DwMetaDataFormStateService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super(uiMetaDataConfig);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    this.attrName = this.fieldDef?.attrName;
    this.dateOptions = this.fieldDef?.controlOptions;
    this.dateTimeControl = this.formGroup?.get(this.attrName);
    this.useGmtDateTimeValue = this.useGmtDateTime();
    this.use24HourTimeValue = this.use24HourTime();
    this.applyAddModeDefault();
  }

  selectPreset(option: PresetDateTimeOption): void {
    if (!this.dateTimeControl || this.dateTimeControl.disabled) {
      return;
    }
    this.applyPresetOption(option, true);
  }

  trackByPresetLabel(index: number, option: PresetDateTimeOption): string {
    return option.key;
  }

  private applyAddModeDefault(): void {
    if (!this.canApplyAddModeDefault()) {
      return;
    }

    if (this.formApi?.isDataLoadComplete?.value) {
      setTimeout(() => this.setAddModeDefault());
      return;
    }

    this.formApi?.isDataLoadComplete
      ?.pipe(
        filter(isComplete => !!isComplete),
        take(1)
      )
      .subscribe(() => setTimeout(() => this.setAddModeDefault()));
  }

  private setAddModeDefault(): void {
    const defaultPresetKey = this.getAddModeDefaultPresetKey();
    const defaultPreset = this.presetOptions.find(option => option.key === defaultPresetKey);

    if (!defaultPreset || !this.shouldApplyAddModeDefault()) {
      return;
    }

    this.applyPresetOption(defaultPreset, false);
    this.addModeDefaultApplied = true;
  }

  private canApplyAddModeDefault(): boolean {
    return !!this.dateTimeControl
      && this.formApi?.formMode === DwFormContextMode.add
      && !!this.getAddModeDefaultPresetKey();
  }

  private shouldApplyAddModeDefault(): boolean {
    return this.canApplyAddModeDefault()
      && !this.dateTimeControl.disabled
      && !this.dateTimeControl.touched
      && !this.addModeDefaultApplied;
  }

  private applyPresetOption(option: PresetDateTimeOption, markDirty: boolean): void {
    const formattedDate = this.getPresetDateTimeValue(option);
    const patchData = { [this.attrName]: formattedDate };

    this.formApi?.patchFormData(patchData);
    this.formApi?.patchContextData('data', patchData);
    this.dateTimeControl.setValue(formattedDate, { emitEvent: markDirty });
    this.selectedPresetKey = option.key;

    if (markDirty) {
      this.dateTimeControl.markAsDirty();
      this.dateTimeControl.markAsTouched();
    } else {
      this.dateTimeControl.markAsPristine();
      this.dateTimeControl.markAsUntouched();
    }

    this.changeDetectorRef.markForCheck();
  }

  private getAddModeDefaultPresetKey(): PresetDateTimeOptionKey | null {
    const defaultPreset = this.dateOptions?.defaultPresetOnAdd;

    if (defaultPreset === false) {
      return null;
    }

    if (defaultPreset) {
      return defaultPreset;
    }

    return this.fieldDef?.itemName === 'Assignment' && this.attrName === 'DueDateTime'
      ? 'next-week'
      : null;
  }

  private getPresetDateTimeValue(option: PresetDateTimeOption): string {
    const presetDate = new Date();
    presetDate.setDate(presetDate.getDate() + option.daysToAdd);
    presetDate.setHours(this.dateOptions?.presetHour ?? 17, this.dateOptions?.presetMinute ?? 0, 0, 0);

    return this.useGmtDateTimeValue
      ? formatDateTimeFromLocal(this.formatLocalDateTime(presetDate, true), true, !this.use24HourTimeValue)
      : this.formatLocalDateTime(presetDate, !this.use24HourTimeValue);
  }

  private formatLocalDateTime(value: Date, showAmPm: boolean): string {
    const month = this.padDatePart(value.getMonth() + 1);
    const day = this.padDatePart(value.getDate());
    const year = value.getFullYear();
    const minutes = this.padDatePart(value.getMinutes());

    if (!showAmPm) {
      return `${month}/${day}/${year} ${this.padDatePart(value.getHours())}:${minutes}`;
    }

    const amPm = value.getHours() >= 12 ? 'PM' : 'AM';
    const hour = value.getHours() % 12 || 12;

    return `${month}/${day}/${year} ${this.padDatePart(hour)}:${minutes} ${amPm}`;
  }

  private padDatePart(value: number): string {
    return value < 10 ? `0${value}` : value.toString();
  }

}
